import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Plus, Trash2 } from "lucide-react";

const ListEditor = ({ value, onChange, fieldKey }) => {
  const update = (i, v) => onChange(value.map((x, idx) => (idx === i ? v : x)));
  const add = () => onChange([...value, ""]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      {value.map((item, i) => (
        <div key={i} className="flex gap-2">
          <Input value={item} onChange={(e) => update(i, e.target.value)} className="bg-slate-700 border-slate-600" data-testid={`${fieldKey}-item-${i}`} />
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} className="text-red-400 shrink-0" data-testid={`${fieldKey}-remove-${i}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="border-slate-600" data-testid={`${fieldKey}-add`}>
        <Plus className="w-4 h-4 mr-1" />
        項目を追加
      </Button>
    </div>
  );
};

const KvListEditor = ({ value, onChange, fieldKey }) => {
  const update = (i, k, v) => onChange(value.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = () => onChange([...value, { label: "", value: "" }]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      {value.map((row, i) => (
        <div key={i} className="flex gap-2">
          <Input placeholder="項目名" value={row.label || ""} onChange={(e) => update(i, "label", e.target.value)} className="bg-slate-700 border-slate-600 sm:w-1/3" data-testid={`${fieldKey}-label-${i}`} />
          <Input placeholder="内容" value={row.value || ""} onChange={(e) => update(i, "value", e.target.value)} className="bg-slate-700 border-slate-600 flex-1" data-testid={`${fieldKey}-value-${i}`} />
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} className="text-red-400 shrink-0" data-testid={`${fieldKey}-remove-${i}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="border-slate-600" data-testid={`${fieldKey}-add`}>
        <Plus className="w-4 h-4 mr-1" />
        行を追加
      </Button>
    </div>
  );
};

const CardsEditor = ({ field, value, onChange }) => {
  const update = (i, k, v) => onChange(value.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = () => {
    const o = {};
    (field.fields || []).forEach((f) => (o[f.key] = ""));
    onChange([...value, o]);
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {value.map((item, i) => (
        <div key={i} className="p-3 rounded-lg border border-slate-600 bg-slate-700/40 space-y-2" data-testid={`${field.key}-card-${i}`}>
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-400">{field.itemLabel || "項目"} {i + 1}</span>
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} className="text-red-400 h-7 w-7" data-testid={`${field.key}-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          {(field.fields || []).map((sf) => (
            <div key={sf.key} className="space-y-1">
              <label className="text-xs text-slate-400">{sf.label}</label>
              {sf.type === "textarea" ? (
                <textarea
                  value={item[sf.key] || ""}
                  onChange={(e) => update(i, sf.key, e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid={`${field.key}-${sf.key}-${i}`}
                />
              ) : (
                <Input value={item[sf.key] || ""} onChange={(e) => update(i, sf.key, e.target.value)} className="bg-slate-700 border-slate-600" data-testid={`${field.key}-${sf.key}-${i}`} />
              )}
            </div>
          ))}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="border-slate-600" data-testid={`${field.key}-add`}>
        <Plus className="w-4 h-4 mr-1" />
        {field.itemLabel || "項目"}を追加
      </Button>
    </div>
  );
};

export const SiteContentEditor = ({ schema, content, onChange }) => {
  const c = content || {};
  const setField = (key, value) => onChange({ ...c, [key]: value });

  if (!schema) return null;

  return (
    <div className="space-y-6" data-testid="site-content-editor">
      {schema.map((field) => (
        <div key={field.key} className="space-y-2">
          <label className="text-sm font-semibold text-slate-200">{field.label}</label>
          {field.type === "text" && (
            <Input value={c[field.key] || ""} onChange={(e) => setField(field.key, e.target.value)} className="bg-slate-700 border-slate-600" data-testid={`field-${field.key}`} />
          )}
          {field.type === "textarea" && (
            <textarea
              value={c[field.key] || ""}
              onChange={(e) => setField(field.key, e.target.value)}
              rows={4}
              className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid={`field-${field.key}`}
            />
          )}
          {field.type === "list" && (
            <ListEditor value={Array.isArray(c[field.key]) ? c[field.key] : []} onChange={(v) => setField(field.key, v)} fieldKey={field.key} />
          )}
          {field.type === "kvlist" && (
            <KvListEditor value={Array.isArray(c[field.key]) ? c[field.key] : []} onChange={(v) => setField(field.key, v)} fieldKey={field.key} />
          )}
          {field.type === "cards" && (
            <CardsEditor field={field} value={Array.isArray(c[field.key]) ? c[field.key] : []} onChange={(v) => setField(field.key, v)} />
          )}
        </div>
      ))}
    </div>
  );
};
